import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Button,
  Spinner,
} from "reactstrap";
import { withTranslation } from "react-i18next";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useParams, useNavigate } from "react-router-dom";
import { resolveImage } from "helpers/config";
import { getVoyageData, availabilityAccept, availabilityRefuse } from "helpers/requests";
import { format } from "date-fns";

const fmtDate = (d) => {
  if (!d) return "—";
  try { return format(new Date(d), "dd MMM yyyy"); } catch { return d; }
};

const InfoItem = ({ label, value, icon, colSize = 6 }) => (
  <Col md={colSize} className="mb-3">
    <div style={{ fontSize: 11, color: "#74788d", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>
      {icon && <i className={`${icon} me-1`} />}
      {label}
    </div>
    <div style={{ fontSize: 15, fontWeight: 500, color: "#495057" }}>{value || "—"}</div>
  </Col>
);

const parseSafe = (val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === "string") {
    try { return JSON.parse(val); } catch { return []; }
  }
  return [];
};

const VoyageDetail = ({ t }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [voyage, setVoyage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await getVoyageData(id);
        if (response) {
          response.departures = parseSafe(response.departures);
          response.excursions = parseSafe(response.excursions);
        }
        setVoyage(response);
      } catch (e) {
        console.error("Error fetching voyage:", e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  const handleToggleAvailability = async () => {
    setToggling(true);
    try {
      if (voyage.availability) {
        await availabilityRefuse(id);
        setVoyage((prev) => ({ ...prev, availability: false }));
      } else {
        await availabilityAccept(id);
        setVoyage((prev) => ({ ...prev, availability: true }));
      }
    } catch (e) {
      console.error("Error toggling availability:", e);
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  if (!voyage) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={t("Voyage Organise")} breadcrumbItem={t("Details")} />
          <Card><CardBody className="text-center py-5"><h5>Voyage not found</h5></CardBody></Card>
        </Container>
      </div>
    );
  }

  const heroImage = voyage.images?.[0]?.url
    ? resolveImage(voyage.images[0].url)
    : null;

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title={t("Voyage Organise")} breadcrumbItem={t("Details")} />

        {/* Header Card */}
        <Card className="mb-3">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: 48, height: 48, borderRadius: 12,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    backgroundColor: voyage.availability ? "#34c38f15" : "#f46a6a15",
                  }}
                >
                  <i
                    className={`mdi ${voyage.availability ? "mdi-airplane-check" : "mdi-airplane-off"} font-size-24`}
                    style={{ color: voyage.availability ? "#34c38f" : "#f46a6a" }}
                  />
                </div>
                <div>
                  <h5 className="mb-0">{voyage.title}</h5>
                  <span className="text-muted font-size-13">
                    {voyage.code} — {voyage.cite}
                  </span>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Badge color={voyage.availability ? "success" : "danger"} className="font-size-12 px-3 py-2">
                  {voyage.availability ? "Available" : "Unavailable"}
                </Badge>
                <Button
                  color={voyage.availability ? "danger" : "success"}
                  size="sm"
                  onClick={handleToggleAvailability}
                  disabled={toggling}
                  className="px-3"
                >
                  {toggling ? (
                    <Spinner size="sm" />
                  ) : voyage.availability ? (
                    <><i className="mdi mdi-close me-1" /> Deactivate</>
                  ) : (
                    <><i className="mdi mdi-check me-1" /> Activate</>
                  )}
                </Button>
                <Button color="light" size="sm" onClick={() => navigate("/voyage-organise")} className="px-3">
                  <i className="mdi mdi-arrow-left me-1" /> Back
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        <Row>
          {/* Left Column */}
          <Col lg={8}>
            {/* Hero Image + Info */}
            <Card className="mb-3 overflow-hidden">
              {heroImage ? (
                <img
                  src={heroImage}
                  alt="Voyage"
                  style={{ width: "100%", height: 280, objectFit: "cover" }}
                />
              ) : (
                <div
                  style={{
                    width: "100%", height: 160,
                    background: "linear-gradient(135deg, #556ee6 0%, #34c38f 100%)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <i className="mdi mdi-airplane font-size-24 text-white me-2" />
                  <span style={{ color: "#fff", fontSize: 20, fontWeight: 600 }}>{voyage.title}</span>
                </div>
              )}
              <CardBody>
                <h5 className="mb-3">
                  <i className="mdi mdi-information-outline me-2 text-primary" />
                  Voyage Information
                </h5>
                <Row>
                  <InfoItem label="Title" value={voyage.title} icon="mdi mdi-format-title" />
                  <InfoItem label="Code" value={voyage.code} icon="mdi mdi-barcode" />
                  <InfoItem label="City" value={voyage.cite} icon="mdi mdi-city" />
                  <InfoItem label="Street" value={voyage.street} icon="mdi mdi-road" />
                  <InfoItem label="Airline" value={voyage.airline} icon="mdi mdi-airplane" />
                  <InfoItem
                    label="Availability"
                    value={
                      <Badge color={voyage.availability ? "success" : "danger"}>
                        {voyage.availability ? "Available" : "Unavailable"}
                      </Badge>
                    }
                    icon="mdi mdi-toggle-switch"
                  />
                </Row>
                {voyage.description && (
                  <>
                    <div style={{ fontSize: 11, color: "#74788d", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4, marginTop: 8 }}>
                      <i className="mdi mdi-text me-1" />Description
                    </div>
                    <p style={{ color: "#495057", lineHeight: 1.7 }}>{voyage.description}</p>
                  </>
                )}
              </CardBody>
            </Card>

            {/* Departures */}
            <Card className="mb-3">
              <CardBody>
                <h5 className="mb-3">
                  <i className="mdi mdi-calendar-range me-2 text-primary" />
                  Departures
                  {voyage.departures?.length > 0 && (
                    <Badge color="light" className="ms-2 text-muted">{voyage.departures.length}</Badge>
                  )}
                </h5>
                {voyage.departures?.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Departure</th>
                          <th>Return</th>
                        </tr>
                      </thead>
                      <tbody>
                        {voyage.departures.map((dep, i) => (
                          <tr key={i}>
                            <td><Badge color="soft-primary" className="font-size-11">{i + 1}</Badge></td>
                            <td>
                              <i className="mdi mdi-airplane-takeoff me-1 text-success" />
                              {fmtDate(dep.start)}
                            </td>
                            <td>
                              <i className="mdi mdi-airplane-landing me-1 text-danger" />
                              {fmtDate(dep.end)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted mb-0">No departures available</p>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Right Column */}
          <Col lg={4}>
            {/* Excursions */}
            <Card className="mb-3">
              <CardBody>
                <h5 className="mb-3">
                  <i className="mdi mdi-hiking me-2 text-primary" />
                  Excursions
                  {voyage.excursions?.length > 0 && (
                    <Badge color="light" className="ms-2 text-muted">{voyage.excursions.length}</Badge>
                  )}
                </h5>
                {voyage.excursions?.length > 0 ? (
                  <div className="d-flex flex-column gap-2">
                    {voyage.excursions.map((exc, i) => {
                      const name = typeof exc === "object" ? exc.name : exc;
                      const icon = typeof exc === "object" && exc.icon ? exc.icon : "mdi mdi-check-circle";
                      return (
                        <div key={i} className="d-flex align-items-center gap-2 py-1" style={{ borderBottom: i < voyage.excursions.length - 1 ? "1px solid #f0f0f0" : "none" }}>
                          <i className={`${icon} text-success font-size-16`} />
                          <span className="font-size-13">{name}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted mb-0">No excursions</p>
                )}
              </CardBody>
            </Card>

            {/* Property */}
            <Card className="mb-3">
              <CardBody>
                <h5 className="mb-3">
                  <i className="mdi mdi-domain me-2 text-primary" />
                  Property
                </h5>
                {voyage.property ? (
                  <>
                    <Row>
                      <InfoItem label="Name" value={voyage.property.name} icon="mdi mdi-office-building" colSize={12} />
                      <InfoItem label="Location" value={voyage.property.state || voyage.property.location} icon="mdi mdi-map-marker" colSize={12} />
                    </Row>
                    {voyage.property.description && (
                      <p className="text-muted font-size-13 mt-1">{voyage.property.description}</p>
                    )}
                    {voyage.property.images?.length > 0 && (
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {voyage.property.images.map((img, i) => (
                          <img
                            key={img.id || i}
                            src={resolveImage(img.url)}
                            alt={`Property ${i + 1}`}
                            className="rounded"
                            style={{ width: 80, height: 60, objectFit: "cover" }}
                          />
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-muted mb-0">No property linked</p>
                )}
              </CardBody>
            </Card>

            {/* Images Gallery */}
            {voyage.images?.length > 1 && (
              <Card className="mb-3">
                <CardBody>
                  <h5 className="mb-3">
                    <i className="mdi mdi-image-multiple me-2 text-primary" />
                    Gallery
                    <Badge color="light" className="ms-2 text-muted">{voyage.images.length}</Badge>
                  </h5>
                  <div className="d-flex flex-wrap gap-2">
                    {voyage.images.map((img, i) => (
                      <a
                        key={img.id || i}
                        href={resolveImage(img.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={resolveImage(img.url)}
                          alt={`Voyage ${i + 1}`}
                          className="rounded"
                          style={{ width: 80, height: 60, objectFit: "cover", cursor: "pointer" }}
                        />
                      </a>
                    ))}
                  </div>
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default withTranslation()(VoyageDetail);
